import { Injectable } from "@angular/core";
import * as moment from "moment";

import {
    FormioComponentSelectors
} from "@features/bb-formio/classes/pdf-generator/formio-components-selectors";
import {
    bodyImageFrontAndBack
} from "@features/bb-formio/components/custom-components/body-map/fixtures/bodymapFrontAndBack";


@Injectable({
    providedIn: "root"
})
export class FormioFormValuesParserService {

    private parser = new DOMParser();
    private html: Document = null;
    private formData: Object = null;

    private classesToRemove = [
        "formio-form-group",
        "form-group",
        "has-feedback",
        "formio-component",
        "formio-component-label-hidden",
        "signature-pad"
    ];

    /** Swap `component` in the live DOM for the first element child of `parsed.body`, if both exist. */
    private replaceWithParsedBodyChild(component: Element, parsed: Document): boolean {
        if (!component || !parsed?.body?.children) return false;

        const parent: HTMLElement = component.parentElement;
        const newChild: Element = parsed.body.children[0] ?? null;

        if (!parent || !newChild) return false;

        parent.replaceChild(newChild, component);

        return true;
    }

    public applyFormValues(doc: Document, formData: any): Document {
        if (!doc || !formData) return doc;

        this.html = doc;

        // For testing
        this.formData = formData;

        this.applyFormValuesToHtml(this.html);

        return this.html;
    }

    private applyFormValuesToHtml(html: Document) {
        if (!html) return;

        const formioComponents = html.getElementsByClassName("formio-component");

        if (formioComponents.length == 0) return;

        while (formioComponents.length > 0) {
            const element = formioComponents[0];

            if (element) {
                let classValues = element?.className?.split(" ") ?? [];

                classValues = classValues.filter(classValue => classValue.trim() !== "" &&
                    !this.classesToRemove.some(valToRemove => valToRemove === classValue));

                element.classList?.remove(...this.classesToRemove);

                const formioComponent = classValues[0];
                const formioComponentIdClass = classValues[1];

                switch (formioComponent) {
                    case FormioComponentSelectors.TextArea:
                        this.handleFormioTextAreaComponent(element);
                        break;
                    case FormioComponentSelectors.SelectBoxes:
                        this.handleFormioSelectBoxesComponent(element, formioComponentIdClass);
                        break;
                    case FormioComponentSelectors.Select:
                        this.handleFormioSelectComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.Radio:
                        this.handleFormioRadioComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.Tags:
                        this.handleFormioTagsComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.Address:
                        this.handleFormioAddressComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.DateTime:
                        this.handleFormioDateTimeComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.Day:
                        this.handleFormioDayComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.Time:
                        this.handleFormioTimeComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    // TODO: handle Currency component
                    case FormioComponentSelectors.Survey:
                        this.handleFormioSurveyComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.Signature:
                        this.handleFormioSignatureComponent(element, formioComponentIdClass ?? formioComponent);
                        break;
                    case FormioComponentSelectors.BodyMap:
                        this.handleFormioBodyMapComponent(element, formioComponentIdClass ?? formioComponent);
                        break;

                    case FormioComponentSelectors.PatientDateOfBirth:
                        this.handleFormioPatientDateOfBirthComponent(element, formioComponentIdClass ?? formioComponent);
                        break;

                    case FormioComponentSelectors.EpisodeNotesDatagrid:
                        this.handleFormioEpisodeNotesDatagridComponent(element, formioComponentIdClass ?? formioComponent);
                        break;

                    case FormioComponentSelectors.MiproConsultHistDatagrid:
                        this.handleFormioMiproConsultHistDatagridComponent(element, formioComponentIdClass ?? formioComponent);
                        break;

                    case FormioComponentSelectors.MiproConsultHistDatagridV2:
                        this.handleFormioMiproConsultHistDatagridComponentV2(element);
                        break;

                    case FormioComponentSelectors.MiproMedHistDatagrid:
                        this.handleFormioMiproMedHistDatagridDatagridComponent(element, formioComponentIdClass ?? formioComponent);
                        break;

                    case FormioComponentSelectors.SignatureField:
                        this.handleFormioSignatureComponent(element, formioComponentIdClass ?? formioComponent);
                        break;

                    case FormioComponentSelectors.PatientAddress:
                        this.handleFormioTextAreaAddressComponent(element);
                        break;

                    case FormioComponentSelectors.ReviewerInput:
                        this.handleFormioReviewerInput(element)
                        break;

                    default:
                        this.handleFormioInputTags(element);
                        break;
                }
            }
        }
    }

    // TODO: Refactor/Rework - TEST other cases - multiples selects in the same form -
    private handleFormioSelectComponent(component: any, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");
            const property = classWithId.split("-").pop();

            if (!property || property === "") return;

            const formValue = this.formData?.[property];

            // Formio Select Component Options Widgets = ChoiceJS | HTML5
            // ChoiceJS Widget option
            const choice = _componentAsDocument.querySelectorAll(`[data-value='${formValue}']`);

            if (choice.length == 0) {
                // HTML5 Widget option
                const selectedOption = _componentAsDocument.querySelectorAll(`[value='${formValue}']`);

                if (selectedOption.length == 0) return;

                const opt = selectedOption[0] as HTMLOptionElement;
                opt?.setAttribute("selected", "true");

                this.replaceWithParsedBodyChild(component, _componentAsDocument);

                return;
            }

            // This div will have one child = the selected choice
            const addChoiceToThisElement = _componentAsDocument.getElementsByClassName("choices__list choices__list--single");
            const choiceList = addChoiceToThisElement[0];
            const chosen = choice[0];

            if (!choiceList || !chosen) return;

            choiceList.appendChild(chosen);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);
        } catch (error) {
            throw error;
        }
    }

    // TODO: Refactor/Rework
    private handleFormioSelectBoxesComponent(component: any, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();

            if (!property || property === "") return;

            const propBucket = this.formData?.[property];

            if (!propBucket || typeof propBucket !== "object") return;

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];

                const checkboxPropertyId = input.id?.split("-")?.pop();

                if (!checkboxPropertyId || checkboxPropertyId === "") continue;

                const formValue = propBucket[checkboxPropertyId];

                if (!!formValue) {
                    input.setAttribute("checked", "true");
                }
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    private handleFormioInputTags(component: any) {
        try {
            if (!component?.outerHTML) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputElement = _componentAsDocument.getElementsByTagName("input")[0];

            if (!inputElement || inputElement.classList.contains(FormioComponentSelectors.MiproMedHistDatagridInput)) {
                return;
            }

            const formValue = this.getFormPropertyValueByDataAtrb(inputElement.name);

            // Prevent inputs with value="[object Object]"
            // When a component reach this point and the formValue is an object
            // That component have to be handled in another method
            if (typeof formValue === "object" && formValue !== null) return;

            switch (inputElement.type) {
                case "text":
                    const textDisplayValue = this.convertToDisplayString(formValue);
                    (inputElement as HTMLInputElement).setAttribute("value", textDisplayValue);
                    break;
                case "email":
                    const emailDisplayValue = this.convertToDisplayString(formValue);
                    (inputElement as HTMLInputElement).setAttribute("value", emailDisplayValue);
                    break;
                case "password":
                    const formValueLength = this.convertToDisplayString(formValue).length;
                    const passwordDisplayValue = "•".repeat(formValueLength);
                    (inputElement as HTMLInputElement).setAttribute("value", passwordDisplayValue);
                    break;
                case "checkbox":
                    if (formValue) inputElement.setAttribute("checked", "true");
                    break;
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    convertToDisplayString(value: any): string {
        if (value === null || value === undefined) {
            return " ";
        }
        return String(value);
    }

    private handleFormioTextAreaComponent(component: any) {
        try {
            if (!component?.outerHTML) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("textarea");

            if (inputsCollection.length == 0) return;

            const textAreaElement = inputsCollection[0];

            const idParts = textAreaElement.id?.split("-");

            const formDataId = idParts?.length > 1 ? idParts[1] : undefined;

            if (!formDataId || formDataId === "") return;

            const formValue = this.formData?.[formDataId];

            (textAreaElement as HTMLTextAreaElement).innerHTML = formValue ?? "";

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    // TODO: Refactor/Rework - TEST other cases - multiples selects in the same form -
    private handleFormioRadioComponent(component: any, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();

            if (!property || property === "") return;

            if (inputsCollection.length == 0) return;

            const formValue = this.formData?.[property];

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];

                const formDataId = input.id?.split("-")?.pop();

                if (formDataId === formValue) {
                    input.setAttribute("checked", "true");
                }
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    // TODO: Refactor/Rework - TEST other cases - multiples selects in the same form -
    private handleFormioTagsComponent(component: any, classWithId: string) {
        try {
            if (!component || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            // Remove input elements, as there is a later method which replaces
            // all of them by spans, and that's not desired here.
            const inputs = _componentAsDocument.querySelectorAll("input");
            inputs.forEach(input => input.remove());

            this.replaceWithParsedBodyChild(component, _componentAsDocument);
        } catch (error) {
            throw error;
        }
    }

    private handleFormioAddressComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const inputsArray = Array.from(inputsCollection)

            let componentFormValue = this.getFormPropertyValueByElementId(classWithId);

            const componentHasModeSwitcherLayer = componentFormValue?.hasOwnProperty("mode");
            const componentMode = componentHasModeSwitcherLayer ? componentFormValue["mode"] : null;

            // If Manual Mode is allowed - we have an additional layer (for Manual Mode config) in the formData
            // This layer has to be skipped
            if (componentHasModeSwitcherLayer) {
                componentFormValue = componentFormValue?.address;

                //Replace for the whole formData to avoid further errors with textField elements
                const formDataId = classWithId.split("-").pop();
                if (formDataId) {
                    const keys = formDataId.split(".");
                    this.setValueInFormData(keys, this.formData, componentFormValue);
                }
            }

            if (inputsCollection.length == 0) return;

            // The Address component has two ways of display an address
            // 1. Manual - several inputs for the user to fill(address1, address2, city, state, country, zip code)
            // 2. Autocomplete = An address from an address provider like GoogleMaps

            if (componentMode == "manual") {
                if (componentFormValue == null || typeof componentFormValue !== "object") return;

                for (const manualInput of inputsArray) {
                    const inputPropKey = this.getKeysFromDataAtrb(manualInput.name).pop();
                    const formValue = inputPropKey != null ? componentFormValue[inputPropKey] : undefined;

                    manualInput.setAttribute("value", formValue ?? "");
                }
            } else {
                const componentPropName = classWithId.split("-").pop();

                const autocompleteAddressInput = inputsArray.find(input =>
                    this.getKeysFromDataAtrb(input.name).pop() === componentPropName
                );

                // The property "display_name" of the object "address" for the Autocomplete Mode
                // Will have the full address from the address provider
                const displayName = componentFormValue?.display_name;

                autocompleteAddressInput?.setAttribute("value", displayName ?? "");
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    /////////////
    // TODO: Refactor/Rework
    private handleFormioDateTimeComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            let _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();

            if (!property || property === "") return;

            // TODO: get format from DateTime component JSON
            const dateFormat = "yyyy-MM-DD hh:mm a";

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];

                const formValue = this.formData?.[property];

                if (formValue) {
                    const date = moment(formValue).format(dateFormat);
                    input.setAttribute("value", date);
                }

            }
            _componentAsDocument = this.removeIconFromDateTimeComponent(_componentAsDocument);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);
        } catch (error) {
            throw error;
        }
    }

    private getNestedProperty(obj: any, key: string) {
        if (!key) return undefined;

        return key.split(".").reduce((o, i) => (o ? o[i] : undefined), obj);
    }

    private autoResizeTextArea(textArea: HTMLTextAreaElement) {
        if (!textArea) return;

        const minRows = 1;

        textArea.style.height = "auto";

        const lines = (textArea.value ?? "").split("\n").length;
        const rows = Math.max(minRows, lines);

        textArea.style.height = `${rows * 1.7}em`;
    }

    private handleFormioPatientDateOfBirthComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            let _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();

            if (property == null || property === "") return;

            const dateFormat = "DD/MM/YYYY";

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];
                const formValue = this.getNestedProperty(this.formData, property);
                const formattedDate = formValue ? moment(formValue).format(dateFormat) : "";
                input.setAttribute("value", formattedDate ?? "");

                if (formValue == "") {
                    input.placeholder = "";
                }
            }

            _componentAsDocument = this.removeIconFromDateTimeComponent(_componentAsDocument);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);
        } catch (error) {
            throw error;
        }
    }


    private handleFormioEpisodeNotesDatagridComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            let _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();
            const dateFormat = "DD-MM-YYYY HH:mm";

            if (!property || property === "") return;

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];
                const id = input.getAttribute("id");
                if (id && id.endsWith("-ENLastUpdatedDate")) {
                    const formValue = this.getNestedProperty(this.formData, property);

                    if (!formValue || !Array.isArray(formValue) || !formValue[0]) continue;

                    const lastUpdatedDate = formValue[0].ENLastUpdatedDate;
                    const formattedDate = lastUpdatedDate ? moment(lastUpdatedDate).format(dateFormat) : "";
                    input.setAttribute("value", formattedDate ?? "");

                    const nextInput = inputsCollection[i + 1];
                    if (nextInput && !nextInput.hasAttribute("id")) {
                        nextInput.setAttribute("value", formattedDate ?? "");
                    }
                }
            }
            _componentAsDocument = this.removeIconFromDateTimeComponent(_componentAsDocument);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }


    private handleFormioMiproConsultHistDatagridComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            let _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();
            const dateFormat = "DD-MM-YYYY HH:mm";

            if (!property || property === "") return;

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];
                const id = input.getAttribute("id");
                if (id && id.endsWith("-CHLastUpdatedDate")) {
                    const formValue = this.getNestedProperty(this.formData, property);

                    if (!formValue || !Array.isArray(formValue) || !formValue[0]) continue;

                    const lastUpdatedDate = formValue[0].CHLastUpdatedDate;
                    const formattedDate = lastUpdatedDate ? moment(lastUpdatedDate).format(dateFormat) : "";
                    input.setAttribute("value", formattedDate ?? "");

                    const nextInput = inputsCollection[i + 1];
                    if (nextInput && !nextInput.hasAttribute("id")) {
                        nextInput.setAttribute("value", formattedDate ?? "");
                    }
                }
            }
            _componentAsDocument = this.removeIconFromDateTimeComponent(_componentAsDocument);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    private handleFormioMiproConsultHistDatagridComponentV2(component: Element) {
        if (!component?.outerHTML) return;

        let _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

        const inputsCollection = _componentAsDocument.getElementsByTagName("input");
        const inputsArray = Array.from(inputsCollection)

        const dateFormat = "DD-MM-YYYY HH:mm";

        if (inputsCollection.length == 0) return;

        inputsArray.forEach((input, index) => {
            const id = input.getAttribute("id");
            if (!id) return;

            const lastUpdatedDate = input.defaultValue;
            const formattedDate = lastUpdatedDate ? moment(lastUpdatedDate).format(dateFormat) : "";

            const nextInput = inputsCollection[index + 1];
            const isNextInputDisplayedInsteadOfCurrent = nextInput && !nextInput.hasAttribute("id");

            if (isNextInputDisplayedInsteadOfCurrent) {
                nextInput.setAttribute("value", formattedDate ?? "");
            }
        })

        _componentAsDocument = this.removeIconFromDateTimeComponent(_componentAsDocument);

        this.replaceWithParsedBodyChild(component, _componentAsDocument);
    }

    private handleFormioMiproMedHistDatagridDatagridComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            let _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();
            const dateFormat = "DD-MM-YYYY HH:mm";

            if (!property || property === "") return;

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];
                const id = input.getAttribute("id");
                input.classList.add(FormioComponentSelectors.MiproMedHistDatagridInput);

                if (id && id.endsWith("-MHPrescribedDateTime")) {
                    const formValue = this.getNestedProperty(this.formData, property);

                    if (!formValue || !Array.isArray(formValue) || !formValue[0]) continue;

                    const lastUpdatedDate = formValue[0].MHPrescribedDateTime;
                    const formattedDate = lastUpdatedDate ? moment(lastUpdatedDate).format(dateFormat) : "";
                    input.setAttribute("value", formattedDate ?? "");

                    const nextInput = inputsCollection[i + 1];
                    if (nextInput && !nextInput.hasAttribute("id")) {
                        nextInput.setAttribute("value", formattedDate ?? "");
                    }
                }
            }
            _componentAsDocument = this.removeIconFromDateTimeComponent(_componentAsDocument);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }


    private handleFormioTextAreaAddressComponent(component: any) {
        try {
            if (!component?.outerHTML) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("textarea");

            if (inputsCollection.length == 0) return;

            const textAreaElement = inputsCollection[0];
            const idParts = textAreaElement.id?.split("-");
            const formDataId = idParts?.length > 1 ? idParts[1] : undefined;

            if (!formDataId || formDataId === "") return;

            const formValue = this.getNestedProperty(this.formData, formDataId);

            textAreaElement.innerHTML = formValue ?? "";

            if (formValue == "") {
                textAreaElement.placeholder = "";
            }

            this.autoResizeTextArea(textAreaElement);

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    private removeIconFromDateTimeComponent(html: Document): Document {
        const classSelector = "input-group-append";

        const elements = html.getElementsByClassName(classSelector);

        const divElement = document.createElement("div");

        for (let index = 0; index < elements.length; index++) {
            const element = elements[index];
            element.parentNode?.replaceChild(divElement, element);
        }

        return html;
    }
    /////////////

    // TODO: Refactor/Rework
    private handleFormioDayComponent(component: any, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            // 1 select for Month
            // 2 inputs => day and year
            // Input day will have id="${formProperty}-day"
            // Input year will have id="${formProperty}-year"
            const property = classWithId.split("-").pop();

            if (property == null || property === "") return;

            const formValue: string = this.formData?.[property];

            // TODO: the element for year/day/month can be an Input or a Select elements, need to check
            const inputForYear = _componentAsDocument.getElementById(`${property}-year`);
            const inputForDay = _componentAsDocument.getElementById(`${property}-day`);
            const typeOfInputForMonth = _componentAsDocument.getElementById(`${property}-month`)

            const valueAsDate = new Date(formValue);

            const year = moment(valueAsDate, true).format("yyyy");
            const day = moment(valueAsDate, true).format("DD");
            const month = moment(valueAsDate, true).format("MM");

            if (inputForDay) {
                inputForDay.setAttribute("value", day ?? "");
            }

            if (inputForYear) {
                inputForYear.setAttribute("value", year.toString() ?? "");
            }

            if (typeOfInputForMonth) {
                if (typeOfInputForMonth.tagName === "SELECT") {
                    const selectElement = (typeOfInputForMonth as HTMLSelectElement);
                    const monthOption = selectElement?.options?.item(+month);

                    monthOption?.setAttribute("selected", "true");
                } else {
                    typeOfInputForMonth.setAttribute("value", month ?? "");
                }
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    // TODO: Refactor/Rework
    private handleFormioTimeComponent(component: any, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const inputsCollection = _componentAsDocument.getElementsByTagName("input");
            const property = classWithId.split("-").pop();

            if (!property || property === "") return;

            if (inputsCollection.length == 0) return;

            for (let i = 0; i < inputsCollection.length; i++) {
                const input = inputsCollection[i];

                const formValue = this.formData?.[property];

                input.setAttribute("value", formValue ?? "");
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    private handleFormioSurveyComponent(component: any, classWithId: string): void {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const property = classWithId.split("-").pop();
            if (!property) return;

            const formValue = this.formData?.[property];
            if (!formValue) return;

            const componentDocument = this.parser.parseFromString(
                component.outerHTML,
                "text/html"
            );

            // Set checked state for each answer in the survey
            Object.entries(formValue).forEach(([questionKey, answer]) => {
                const inputId = `${property}-${questionKey}-${answer}`;
                const radioInput = componentDocument.getElementById(inputId) as HTMLInputElement | null;

                if (radioInput?.type === "radio") {
                    radioInput.setAttribute("checked", "true");
                }
            });

            this.replaceWithParsedBodyChild(component, componentDocument);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            throw new Error(`Failed to handle survey component: ${message}`);
        }
    }

    private handleFormioSignatureComponent(component: any, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const idTail = classWithId.split("-").pop();
            if (!idTail) return;

            const formDataId = idTail.split(".");
            if (!formDataId.length) return;

            let formValue = this.formData?.[formDataId[0]];

            for (let i = 1; i < formDataId.length; i++) {
                if (formValue == null) return;

                formValue = formValue[formDataId[i]];
            }

            const element = _componentAsDocument.querySelector(".signature-pad-footer");
            if (!!element) element.remove();

            const _img = _componentAsDocument.getElementsByTagName("img")[0];
            const _canvas = _componentAsDocument.getElementsByTagName("canvas")[0];

            if (!_img || !_canvas || formValue == null) return;

            _img.style.display = "inherit";
            _img.setAttribute("src", String(formValue));
            _img.style.height = "100%";
            _img.style.maxWidth = "420px";
            _canvas.style.display = "none";

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    private handleFormioReviewerInput(component: any) {
        try {
            if (!component?.outerHTML) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");
            const inputElement = _componentAsDocument.getElementsByTagName("input")[0];

            if (inputElement) {
                const formValue = this.getFormPropertyValueByDataAtrb(inputElement.name);

                const displaySource = formValue != null && typeof formValue === "object" && "value" in formValue
                    ? (formValue as { value?: unknown }).value
                    : formValue;
                const textDisplayValue = this.convertToDisplayString(displaySource);

                (inputElement as HTMLInputElement).setAttribute("value", textDisplayValue);
            }

            this.replaceWithParsedBodyChild(component, _componentAsDocument);
        } catch (error) {
            throw error;
        }
    }

    private handleFormioBodyMapComponent(component: Element, classWithId: string) {
        try {
            if (!component?.outerHTML || !classWithId) return;

            const _componentAsDocument = this.parser.parseFromString(component.outerHTML, "text/html");

            const formDataId = classWithId.split("-").pop();
            if (!formDataId || formDataId === "") return;

            let propertyValue = this.recursivelyGetPropertyValue(this.formData, formDataId);

            if (propertyValue == "" || !propertyValue) {
                propertyValue = bodyImageFrontAndBack;
            }

            const _img = _componentAsDocument.getElementsByTagName("img")[0];
            const _canvas = _componentAsDocument.getElementsByTagName("canvas")[0];

            if (!_img || !_canvas) return;

            _img.style.display = "inherit";
            _img.setAttribute("src", propertyValue);
            _img.style.height = "100%";
            _canvas.style.display = "none";

            this.replaceWithParsedBodyChild(component, _componentAsDocument);

        } catch (error) {
            throw error;
        }
    }

    private recursivelyGetPropertyValue(obj: object, propertyName: string): string {
        let result = "";

        if (!obj || typeof obj !== "object" || !propertyName) return result;

        for (const [key, value] of Object.entries(obj)) {
            if (key === propertyName) {
                result += value;
            }

            if (typeof value === "object" && value !== null) {
                result += this.recursivelyGetPropertyValue(value, propertyName);
            }
        }

        return result;
    }

    private getFormPropertyValueByElementId(elementId: string): any {
        if (!elementId) return null;

        const formDataId = elementId.split("-").pop();
        if (!formDataId) return null;

        const keys = formDataId.split(".");
        return this.getValueFromFormData(keys, this.formData);
    }

    private getFormPropertyValueByDataAtrb(dataAtrbValue: string): any {
        if (dataAtrbValue == null || dataAtrbValue === "") return null;

        const keys = this.getKeysFromDataAtrb(dataAtrbValue);
        return this.getValueFromFormData(keys, this.formData);
    }

    private getKeysFromDataAtrb(dataAtrbValue: string): string[] {
        if (dataAtrbValue == null || dataAtrbValue === "") return [];

        const pattern = /\[(.*?)\]/g;
        const matches = [...dataAtrbValue.matchAll(pattern)].map(match => match[1]);

        if (!matches) return [];

        let keys: string[] = [];
        for (const match of matches) {
            keys.push(...match.split("."));
        }

        return keys;
    }

    private getValueFromFormData(keys: string[], formData: any): any {
        if (!keys?.length) return formData;

        let formValue = formData;
        for (const key of keys) {
            if (!formValue) return null;
            formValue = formValue[key];
        }
        return formValue;
    }

    private setValueInFormData(keys: string[], formData: any, newValue: any): any {
        if (!keys?.length || !formData) return formData;

        let currentLevel = formData;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (key == null || key === "") return formData;

            if (!currentLevel[key]) {
                currentLevel[key] = {};
            }

            currentLevel = currentLevel[key];
        }

        const finalKey = keys[keys.length - 1];

        if (!finalKey || finalKey === "") return formData;

        currentLevel[finalKey] = newValue;

        return formData;
    }

}
